import React, { useContext, useEffect, useRef, useState } from "react";

import { FormInput } from "../FormInput";
import { useForm } from "react-hook-form";
import { validateJSON } from "../../lib/validator";

import PrimaryButton from "../../tailwindUI/PrimaryButton";
import { AlertContext } from "../../context/AlertContext";
import Alert from "../../tailwindUI/Alert";
import { cn } from "../../util";
import { Editor } from "@monaco-editor/react";
import {
  createSettings,
  getSettings,
  updateSettings,
} from "../../apiClient/operations/settingOperations";
import SecondaryLoader from "../../hooks/SecondaryLoader";
import ButtonOrange from "../../tailwindUI/ButtonOrange";

const getDocumentJSON = (settings, collectionId, documentId) => {
  if (!settings || !collectionId || !documentId) return "{}";

  const value =
    settings[collectionId]?.find((doc) => doc?.id === documentId) || {};

  return JSON.stringify(value, null, 2);
};

const initialValue = {
  status: "initial",
  settings: {},
  jsonValue: null,
  editorErrors: [],
  validationStatus: "waiting",
};

export function Settings() {
  const editorRef = useRef(null);
  const [state, setState] = useState(initialValue);
  const { settings, editorErrors, validationStatus } = state;
  const isLoading = state.status === "loading";
  const isPending = state.status === "pending";

  const { setAlert } = useContext(AlertContext);

  const collectionOptions = Object.keys(settings).map((collectionId) => ({
    label: collectionId,
    value: collectionId,
  }));

  const documentOptions = Object.fromEntries(
    Object.entries(settings).map(([collectionId, documents]) => {
      const otherOption = { label: "Otro", value: "other" };
      const options = documents.map((document) => ({
        label: document.id,
        value: document.id,
      }));
      const newOptions =
        collectionId === "settings" ? options : [...options, otherOption];
      return [collectionId, newOptions];
    }),
  );

  const {
    register,
    watch,
    handleSubmit,
    trigger,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    values: { collectionId: "", documentId: "", newId: "" },
  });

  const selectedCollectionId = watch("collectionId");
  const selectedDocumentId = watch("documentId");
  const selectedNewId = watch("newId");

  const handleValidation = () => {
    trigger();
    validateEditorValue();
  };

  const validateEditorValue = () => {
    try {
      const editorValue = editorRef.current.getValue();
      if (!editorValue) {
        throw new Error();
      }
      const parsed = JSON.parse(editorValue);
      const { errors } = validateJSON(parsed);

      let isValidId = true;
      let message = "";
      if (
        selectedDocumentId !== "" &&
        selectedDocumentId !== "other" &&
        selectedDocumentId !== parsed.id
      ) {
        isValidId = false;
        message = "*El ID debe ser el mismo que el del JSON";
      } else if (
        selectedDocumentId === "other" &&
        selectedNewId !== parsed.id
      ) {
        isValidId = false;
        message = "*El nuevo ID debe ser el mismo que el del JSON";
      }

      const totalErrors = isValidId ? errors : [...errors, { message }];

      setState((prev) => ({
        ...prev,
        jsonValue: parsed,
        validationStatus: totalErrors.length > 0 ? "error" : "success",
        editorErrors: totalErrors.map(
          (err) =>
            `${err.message}: ${JSON.stringify(err?.params || {}, null, 2)}`,
        ),
      }));
    } catch (e) {
      setState((prev) => ({
        ...prev,
        editorErrors: ["Invalid JSON format"],
        validationStatus: "error",
        jsonValue: null,
      }));
    }
  };

  const onSubmit = async () => {
    try {
      setState((prev) => ({ ...prev, status: "pending" }));

      const editorValue = editorRef.current.getValue();
      const editorJSONValue = JSON.parse(editorValue);

      if (selectedNewId) {
        const newDoc = await createSettings(
          selectedCollectionId,
          editorJSONValue,
        );
        setState((prev) => ({ ...prev, status: "document-created", newDoc }));
      } else {
        const newDoc = await updateSettings(
          selectedCollectionId,
          selectedDocumentId,
          editorJSONValue,
        );
        setState((prev) => ({ ...prev, status: "document-updated", newDoc }));
      }
    } catch (error) {
      setState((prev) => ({ ...prev, status: "error-submit" }));
    }
  };

  useEffect(() => {
    setState((prev) => ({ ...prev, status: "loading" }));
    getSettings()
      .then((res) => {
        setState((prev) => ({
          ...prev,
          status: "success-settings",
          settings: res.data,
        }));
      })
      .catch((error) => {
        console.log({ error });
        setState((prev) => ({
          ...prev,
          status: "error-settings",
        }));
      });
  }, []);

  useEffect(() => {
    if (state.status === "error-settings" || state.status === "error-submit") {
      setAlert({
        active: true,
        type: "error",
        message: "Error al procesar tu solicitud",
      });
    } else if (state.status === "document-created") {
      const newDoc = state.newDoc;
      if (!newDoc || !newDoc.id) {
        setAlert({
          active: true,
          type: "error",
          message: "Error al crear el documento",
        });
        return;
      }
      const settingsUpdated = {
        ...settings,
        [selectedCollectionId]: [
          state.newDoc,
          ...settings[selectedCollectionId],
        ],
      };
      setState((prev) => ({ ...prev, settings: settingsUpdated }));
      // setValue("documentId", newDoc.id);
      // setValue("newId", "");
      setAlert({
        active: true,
        type: "success",
        message: "Documento creado correctamente",
      });
    } else if (state.status === "document-updated") {
      const settingsUpdated = {
        ...settings,
        [selectedCollectionId]: settings[selectedCollectionId].map((doc) =>
          doc.id === selectedDocumentId ? state.newDoc : doc,
        ),
      };
      setState((prev) => ({ ...prev, settings: settingsUpdated }));
      setAlert({
        active: true,
        type: "success",
        message: "Documento actualizado correctamente",
      });
    }
  }, [state.status]);

  useEffect(() => {
    if (state.status === "document-created") {
      const newDoc = state.newDoc;
      setValue("documentId", newDoc.id);
      setValue("newId", "");
    }
  }, [state.settings]);

  useEffect(() => {
    if (!editorRef?.current) return;
    const newValue = getDocumentJSON(
      settings,
      selectedCollectionId,
      selectedDocumentId,
    );
    editorRef.current.setValue(newValue);
    setState((prev) => ({ ...prev, editorValue: newValue, editorErrors: [] }));
  }, [selectedDocumentId]);

  useEffect(() => {
    clearErrors();
    setState((prev) => ({ ...prev, validationStatus: "waiting" }));
  }, [selectedCollectionId, selectedDocumentId, selectedNewId]);

  return (
    <>
      {isPending && <SecondaryLoader />}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-3 gap-6 flex-1 py-6"
      >
        <div className="h-full flex flex-col">
          <div>
            <FormInput
              name="collectionId"
              variant="select"
              label="Colección"
              options={collectionOptions}
              errors={errors}
              register={register}
              isLoading={isLoading}
              disabled={state.status === "error-settings"}
              rules={{
                required: "*Se debe seleccinar una collección para continuar",
              }}
            />
            <FormInput
              name="documentId"
              variant="select"
              label="Id"
              options={documentOptions?.[selectedCollectionId] || []}
              errors={errors}
              register={register}
              isLoading={isLoading}
              disabled={state.status === "error-settings"}
              rules={{
                required: "*Se debe seleccinar un documento id para continuar",
              }}
            />
            {selectedDocumentId === "other" && (
              <FormInput
                name="newId"
                variant="input"
                label="Nuevo Id"
                errors={errors}
                register={register}
                isLoading={isLoading}
                rules={{
                  required: "*El nuevo ID es obligatorio",
                }}
              />
            )}
          </div>

          <div className="flex flex-col justify-between border-t border-gray-300 mt-2 py-2">
            <div>
              <h3 className="font-medium text-gray-500 text-sm mb-2">
                Validación de JSON
              </h3>
              {validationStatus === "waiting" && (
                <Alert title="Se debe validar el JSON" type="Info" show />
              )}
              {validationStatus === "error" && (
                <div className="space-y-4">
                  <Alert title={"JSON no válido"} type={"Error"} show />
                  <ul className="text-red-600 text-sm">
                    {editorErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              {validationStatus === "success" && (
                <Alert title="JSON válido" type="Success" show />
              )}
            </div>
            <div className="space-x-4 flex mt-6">
              <ButtonOrange type="button" onClick={handleValidation}>
                Validar JSON
              </ButtonOrange>
              <PrimaryButton
                type="submit"
                disabled={validationStatus !== "success"}
              >
                {selectedDocumentId === "other"
                  ? "Crear documento"
                  : "Actualizar documento"}
              </PrimaryButton>
            </div>
          </div>
        </div>
        <div
          className={cn(
            "col-span-2 h-full w-full flex transition-all rounded-sm",
            "border border-v2-gray-border-tables focus:ring-v2-blue-text-login focus:border-v2-blue-text-login ",
            validationStatus === "error" && "border-red-500",
          )}
        >
          <Editor
            height="100%"
            width="100%"
            defaultLanguage="json"
            language="json"
            defaultValue="{}"
            onChange={() =>
              setState((prev) => ({ ...prev, validationStatus: "waiting" }))
            }
            onMount={(editor) => {
              editorRef.current = editor;
            }}
            options={{
              fontSize: 14,
              tabSize: 2,
              insertSpaces: true,
              automaticLayout: true,
              minimap: {
                enabled: false,
              },
              scrollbar: { horizontal: "hidden" },
              wordWrap: "on",
              renderWhitespace: "none",
              lineNumbers: "on",
              formatOnType: true,
              formatOnPaste: true,
            }}
          />
        </div>
      </form>
    </>
  );
}
